// src/pages/TrainingCharacterization.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import Button from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input, Textarea } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';

const API_BASE = process.env.REACT_APP_API_URL;

export default function TrainingCharacterization() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [motion, setMotion] = useState('');
  const [loadingMotion, setLoadingMotion] = useState(false);

  const [actors, setActors] = useState([
    { name: '', characterization: '', argument: '' },
  ]);

  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [error, setError] = useState('');

  // Optional AI example after feedback
  const [exampleLoading, setExampleLoading] = useState(false);
  const [aiExample, setAiExample] = useState(null);

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  const newMotion = async () => {
    setError('');
    setFeedback(null);
    setAiExample(null);
    setActors([{ name: '', characterization: '', argument: '' }]);
    setLoadingMotion(true);
    try {
      const res = await fetch(`${API_BASE}/api/generate-motion`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate motion');
      setMotion(data.motion);
    } catch (e) {
      console.error(e);
      setError('Could not generate a motion.');
    } finally {
      setLoadingMotion(false);
    }
  };

  const updateActor = (i, field, value) => {
    setActors(prev => prev.map((a, idx) => (idx === i ? { ...a, [field]: value } : a)));
  };
  const addActor = () => setActors(prev => [...prev, { name: '', characterization: '', argument: '' }]);
  const removeActor = (i) => setActors(prev => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    setError('');
    setFeedback(null);
    setAiExample(null);

    if (!motion) {
      setError('Generate a motion first.');
      return;
    }

    const clean = actors
      .map(a => ({
        name: a.name.trim(),
        characterization: a.characterization.trim(),
        argument: a.argument.trim(),
      }))
      .filter(a => a.name && a.characterization && a.argument);

    if (clean.length === 0) {
      setError('Add at least one actor with characterization and a linked argument.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/characterization-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ motion, actors: clean }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to evaluate characterization');
      setFeedback(data);
    } catch (e) {
      console.error(e);
      setError('Failed to get feedback.');
    } finally {
      setSubmitting(false);
    }
  };

  const getAIExample = async () => {
    if (!motion || !feedback) return;
    setExampleLoading(true);
    setAiExample(null);
    try {
      // If your route name differs, adjust here (e.g., /api/characterization-example)
      const res = await fetch(`${API_BASE}/api/characterization-example`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ motion }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate example');
      setAiExample(data);
    } catch (e) {
      console.error(e);
      setError('Failed to generate AI example.');
    } finally {
      setExampleLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Characterization Training</h1>
          <p className="text-sm text-gray-600">
            Add actors, characterize them, and give a brief argument that clearly follows from the characterization.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={newMotion} disabled={loadingMotion}>
            {loadingMotion ? <span className="inline-flex items-center gap-2"><Spinner />Generating…</span> : 'New Motion'}
          </Button>
          <Button variant="secondary" onClick={() => navigate('/training')}>Back to Training</Button>
        </div>
      </div>

      {/* Motion */}
      <Card title="Practice Setup" subtitle="Your motion for this drill">
        <label className="block text-xs font-medium text-gray-600 mb-1">Motion</label>
        <div className="rounded-lg border bg-gray-50 px-3 py-2 text-sm text-gray-800 min-h-[40px]">
          {motion || 'Click "New Motion" to begin.'}
        </div>
      </Card>

      {/* Actors */}
      <Card title="Actors and Characterization" subtitle="Define actors, characterize them, and show how that leads to an argument">
        <div className="space-y-6">
          {actors.map((actor, i) => (
            <div key={i} className="rounded-lg border border-gray-200 p-4">
              <div className="grid gap-4 sm:grid-cols-6">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Actor Name</label>
                  <Input
                    placeholder="e.g., Low-income workers"
                    value={actor.name}
                    onChange={(e) => updateActor(i, 'name', e.target.value)}
                  />
                </div>
                <div className="sm:col-span-4">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Characterization</label>
                  <Textarea
                    rows={2}
                    placeholder="Incentives, constraints, likely behavior, and what they care about"
                    value={actor.characterization}
                    onChange={(e) => updateActor(i, 'characterization', e.target.value)}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-xs font-medium text-gray-600 mb-1">Argument From This Characterization</label>
                <Textarea
                  rows={2}
                  placeholder="A concise argument that follows from the characterization"
                  value={actor.argument}
                  onChange={(e) => updateActor(i, 'argument', e.target.value)}
                />
              </div>

              <div className="mt-3 flex justify-between">
                <span className="text-xs text-gray-500">Tip: Keep arguments brief but clearly connected to the traits you listed.</span>
                {actors.length > 1 && (
                  <Button variant="ghost" className="text-red-600" onClick={() => removeActor(i)}>
                    Remove
                  </Button>
                )}
              </div>
            </div>
          ))}

          <div className="flex items-center justify-between">
            <Button variant="secondary" onClick={addActor}>+ Add Actor</Button>
            <Button onClick={submit} disabled={submitting}>
              {submitting ? <span className="inline-flex items-center gap-2"><Spinner />Evaluating…</span> : 'Submit for Feedback'}
            </Button>
          </div>

          {error && (
            <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>
      </Card>

      {/* Feedback */}
      {feedback && (
        <Card title="AI Feedback" subtitle="Scores and comments per actor, plus overall notes">
          {/* If your backend returns { overall_comments, actors: [{name, relevance, characterization, linkage, score, comments}] } */}
          {Array.isArray(feedback.actors) && feedback.actors.length > 0 ? (
            <div className="space-y-4">
              {feedback.actors.map((a, idx) => (
                <div key={idx} className="rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-900">{a.name || `Actor ${idx + 1}`}</h4>
                    <div className="text-sm text-gray-700">
                      {a.relevance !== undefined && <span className="mr-4"><strong>Relevance:</strong> {a.relevance}/10</span>}
                      {a.characterization_score !== undefined && <span className="mr-4"><strong>Characterization:</strong> {a.characterization_score}/10</span>}
                      {a.linkage !== undefined && <span className="mr-4"><strong>Linkage:</strong> {a.linkage}/10</span>}
                      {a.score !== undefined && <span><strong>Overall:</strong> {a.score}/10</span>}
                    </div>
                  </div>
                  {a.comments && (
                    <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">{a.comments}</p>
                  )}
                </div>
              ))}

              {feedback.missed_key_actors && feedback.missed_key_actors.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <h4 className="font-semibold text-amber-900 mb-1">Potential Key Actors You Missed</h4>
                  <p className="text-sm text-amber-900">{feedback.missed_key_actors.join(', ')}</p>
                </div>
              )}

              {feedback.overall_comments && (
                <div className="rounded-lg border border-gray-200 p-4">
                  <h4 className="font-semibold text-gray-900 mb-1">Overall</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{feedback.overall_comments}</p>
                </div>
              )}

              <div className="pt-2">
                <Button onClick={getAIExample} disabled={exampleLoading}>
                  {exampleLoading ? <span className="inline-flex items-center gap-2"><Spinner />Generating example…</span> : 'Generate AI Example'}
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-700">No actor-level feedback returned.</p>
          )}
        </Card>
      )}

      {aiExample && (
        <Card title="AI Characterization Example" subtitle="A compact example added after your feedback">
          {/* Expecting something like:
              { actors: [{ name, characterization, argument }], notes: "..." } */}
          <div className="space-y-3">
            {Array.isArray(aiExample.actors) && aiExample.actors.map((a, i) => (
              <div key={i} className="rounded-lg border border-gray-200 p-4">
                <p className="text-sm"><strong>Actor:</strong> {a.name}</p>
                <p className="text-sm"><strong>Characterization:</strong> {a.characterization}</p>
                <p className="text-sm"><strong>Argument:</strong> {a.argument}</p>
              </div>
            ))}
            {aiExample.notes && <p className="text-sm text-gray-700 whitespace-pre-wrap">{aiExample.notes}</p>}
          </div>
        </Card>
      )}
    </div>
  );
}
